import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Save, Trash2 } from "lucide-react";
import { authHeaders } from "../pages/AdminPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATS = [
  "Laces & Trims",
  "Beads & Sequins",
  "Fabrics",
  "Sewing Supplies",
  "Art Supplies",
  "Craft Kits",
  "Accessories",
];

const cellCls =
  "w-full bg-transparent border-b border-transparent focus:border-[#C5A059] px-1 py-1.5 text-sm text-[#2D1B36] transition-colors duration-300";

export const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [newP, setNewP] = useState({ name: "", category: CATS[0], price: "", unit: "each", image: "" });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
      setDrafts(
        Object.fromEntries(
          res.data.map((p) => [
            p.id,
            { name: p.name, category: p.category, price: p.price, unit: p.unit, image: p.image, description: p.description || "" },
          ])
        )
      );
    } catch {
      toast.error("Could not load products");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (id) => {
    try {
      await axios.put(`${API}/admin/products/${id}`, { ...drafts[id], price: Number(drafts[id].price) }, { headers: authHeaders() });
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this product from the store?")) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`, { headers: authHeaders() });
      setProducts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Product removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/products`, { ...newP, price: Number(newP.price) }, { headers: authHeaders() });
      setNewP({ name: "", category: CATS[0], price: "", unit: "each", image: "" });
      toast.success("Product added");
      load();
    } catch {
      toast.error("Could not add product");
    }
  };

  if (loading)
    return (
      <p className="py-20 text-center font-serif-d italic text-xl text-[#8A818F]">Loading products…</p>
    );

  return (
    <div data-testid="products-tab">
      <form onSubmit={add} className="bg-[#FBF9F6] border border-[#E8E4DF] p-6 mb-8" data-testid="add-product-form">
        <p className="font-accent text-[10px] tracking-[0.3em] uppercase text-[#8A818F] mb-5">Add a product</p>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <input
            required
            placeholder="Product name"
            value={newP.name}
            onChange={(e) => setNewP({ ...newP, name: e.target.value })}
            data-testid="new-product-name"
            className={`${cellCls} border-[#E8E4DF] col-span-2`}
          />
          <select
            value={newP.category}
            onChange={(e) => setNewP({ ...newP, category: e.target.value })}
            data-testid="new-product-category"
            className={`${cellCls} border-[#E8E4DF]`}
          >
            {CATS.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
          <input
            required
            type="number"
            min="1"
            placeholder="Price ₹"
            value={newP.price}
            onChange={(e) => setNewP({ ...newP, price: e.target.value })}
            data-testid="new-product-price"
            className={`${cellCls} border-[#E8E4DF]`}
          />
          <input
            placeholder="Unit (e.g. per metre)"
            value={newP.unit}
            onChange={(e) => setNewP({ ...newP, unit: e.target.value })}
            data-testid="new-product-unit"
            className={`${cellCls} border-[#E8E4DF]`}
          />
          <button
            type="submit"
            data-testid="new-product-submit"
            className="flex items-center justify-center gap-2 bg-[#4A2B58] text-[#FBF9F6] font-accent text-[10px] tracking-[0.2em] uppercase py-2.5 hover:bg-[#2D1B36] transition-colors"
          >
            <Plus size={13} /> Add
          </button>
        </div>
      </form>

      <div className="space-y-3" data-testid="products-list">
        {products.map((p) => {
          const d = drafts[p.id] || {};
          const set = (k, v) => setDrafts((prev) => ({ ...prev, [p.id]: { ...prev[p.id], [k]: v } }));
          return (
            <div
              key={p.id}
              data-testid={`admin-product-${p.id}`}
              className="bg-[#FBF9F6] border border-[#E8E4DF] p-4 flex flex-wrap items-center gap-4"
            >
              <img src={d.image} alt="" className="w-12 h-14 object-cover bg-[#F2EFEB] shrink-0" />
              <input
                value={d.name || ""}
                onChange={(e) => set("name", e.target.value)}
                data-testid={`admin-product-name-${p.id}`}
                className={`${cellCls} flex-1 min-w-[180px]`}
              />
              <select
                value={d.category}
                onChange={(e) => set("category", e.target.value)}
                data-testid={`admin-product-category-${p.id}`}
                className={`${cellCls} w-40`}
              >
                {CATS.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
              <div className="flex items-center gap-1 w-28">
                <span className="text-sm text-[#8A818F]">₹</span>
                <input
                  type="number"
                  min="1"
                  value={d.price ?? ""}
                  onChange={(e) => set("price", e.target.value)}
                  data-testid={`admin-product-price-${p.id}`}
                  className={cellCls}
                />
              </div>
              <input
                value={d.unit || ""}
                onChange={(e) => set("unit", e.target.value)}
                data-testid={`admin-product-unit-${p.id}`}
                className={`${cellCls} w-32`}
              />
              <div className="flex gap-2 ml-auto">
                <button
                  onClick={() => save(p.id)}
                  data-testid={`admin-product-save-${p.id}`}
                  className="flex items-center gap-1.5 bg-[#C5A059] text-[#2D1B36] px-4 py-2 font-accent text-[10px] tracking-[0.2em] uppercase hover:bg-[#DFCA9B] transition-colors"
                >
                  <Save size={12} /> Save
                </button>
                <button
                  onClick={() => del(p.id)}
                  data-testid={`admin-product-delete-${p.id}`}
                  className="p-2 text-[#8A818F] hover:text-red-700 transition-colors"
                >
                  <Trash2 size={15} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
